import { WORKFLOW_SCHEDULER_TYPES, WORKFLOW_WERKER_TYPES } from "../functions/postActionTypes";

// ─── Event type used by react-big-calendar ───────────────────────────────────

export interface CalendarJobEvent {
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource: CalendarJobResource;
}

/** Minimal shape needed by the calendar and detail panel */
export interface CalendarJobResource {
  /** The uniqueJobId for schedulers; same field for werkers */
  jobDocId: string;
  /** Underlying Firestore doc id (werker-side MyJobDocId or scheduler's JobDocId) */
  firestoreDocId: string;
  role: "scheduler" | "werker";
  company: string;
  location: string;
  pay: string;
  shiftIdentifier: string;
  startDateTimeRaw: string;
  finishDateTimeRaw: string;
  jobStatusSched: string;
  jobStatusWerker: string;
  isCancelled: boolean;
  schedUid?: string;
  seriesId?: string | null;
  seriesIndex?: number | null;
  seriesTotal?: number | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseDate(raw: any): Date | null {
  if (!raw) return null;

  // Firestore Timestamp
  if (typeof raw?.toMillis === "function") {
    const ms = raw.toMillis();
    return Number.isFinite(ms) ? new Date(ms) : null;
  }

  if (typeof raw === "number" && Number.isFinite(raw)) return new Date(raw);

  if (typeof raw === "string") {
    // Strip tz designator if needed so JS treats it as local
    const d = new Date(raw.substring(0, 19));
    return Number.isFinite(d.getTime()) ? d : null;
  }

  return null;
}

function isDeletedSched(status: string): boolean {
  return status === WORKFLOW_SCHEDULER_TYPES.Deleted;
}

function isDeletedWerker(status: string): boolean {
  return status === WORKFLOW_WERKER_TYPES.Deleted;
}

function stripWeekday(value: string): string {
  if (!value) return "";

  // Remove weekday words if present in shift labels (e.g. "Monday", "Mon")
  const withoutWeekdays = value.replace(
    /\b(monday|mon|tuesday|tue|tues|wednesday|wed|thursday|thu|thur|thurs|friday|fri|saturday|sat|sunday|sun)\b/gi,
    "",
  );

  return withoutWeekdays.replace(/\s{2,}/g, " ").trim();
}

// ─── Scheduler job mapper ───────────────────────────────────────────────────
// Input: elements from SchedJobContext.schedJob

export function schedJobsToEvents(schedJobs: any[]): CalendarJobEvent[] {
  const events: CalendarJobEvent[] = [];

  for (const job of schedJobs) {
    if (isDeletedSched(job.JobStatusSched)) continue;

    const start = parseDate(job.StartDateTimeRaw);
    const end = parseDate(job.FinishDateTimeRaw);
    if (!start || !end) continue;

    const company = job.Company || "";
    const identifier = stripWeekday(job.ShiftIdentifier || "");
    let title = [company, identifier].filter(Boolean).join(" · ") || "Job";
    if (job.seriesIndex != null && job.seriesTotal != null && job.seriesTotal > 1) {
      title += ` (${job.seriesIndex}/${job.seriesTotal})`;
    }

    events.push({
      title,
      start,
      end,
      resource: {
        jobDocId: job.JobDocId,
        firestoreDocId: job.JobDocId,
        role: "scheduler",
        company,
        location: job.Location || "",
        pay: job.Pay || "",
        shiftIdentifier: identifier,
        startDateTimeRaw: job.StartDateTimeRaw || "",
        finishDateTimeRaw: job.FinishDateTimeRaw || "",
        jobStatusSched: job.JobStatusSched || "",
        jobStatusWerker: job.JobStatusWerker || "",
        isCancelled: job.JobCancelledByScheduler === true,
        seriesId: job.seriesId ?? null,
        seriesIndex: job.seriesIndex ?? null,
        seriesTotal: job.seriesTotal ?? null,
      },
    });
  }

  return events;
}

// ─── Werker job mapper ───────────────────────────────────────────────────────
// Input: combined invitedFeedJobs + werkerJob arrays from WerkerJobContext

export function werkerJobsToEvents(werkerJobs: any[]): CalendarJobEvent[] {
  const events: CalendarJobEvent[] = [];
  const seen = new Set<string>();

  for (const job of werkerJobs) {
    if (isDeletedWerker(job.JobStatusWerker)) continue;

    // Deduplicate: invitedFeedJobs and werkerJob can both contain the same job
    const key = job.MyJobDocId || job.JobDocId;
    if (seen.has(key)) continue;
    seen.add(key);

    const start = parseDate(job.StartDateTimeRaw);
    const end = parseDate(job.FinishDateTimeRaw);
    if (!start || !end) continue;

    const company = job.Company || "";
    const identifier = stripWeekday(job.ShiftIdentifier || "");
    let title = [company, identifier].filter(Boolean).join(" · ") || "Job";
    if (job.seriesIndex != null && job.seriesTotal != null && job.seriesTotal > 1) {
      title += ` (${job.seriesIndex}/${job.seriesTotal})`;
    }

    events.push({
      title,
      start,
      end,
      resource: {
        jobDocId: job.JobDocId,
        firestoreDocId: job.MyJobDocId || job.JobDocId,
        role: "werker",
        company,
        location: job.Location || "",
        pay: job.Pay || "",
        shiftIdentifier: identifier,
        startDateTimeRaw: job.StartDateTimeRaw || "",
        finishDateTimeRaw: job.FinishDateTimeRaw || "",
        jobStatusSched: job.JobStatusSched || "",
        jobStatusWerker: job.JobStatusWerker || "",
        isCancelled: job.JobCancelledByScheduler === true,
        schedUid: job.SchedUid || "",
      },
    });
  }

  return events;
}

// ─── Event style getter ──────────────────────────────────────────────────────

interface EventStyleResult {
  style: React.CSSProperties;
}

export function getEventStyle(event: CalendarJobEvent): EventStyleResult {
  const { resource } = event;

  let backgroundColor: string;
  let textColor = "#ffffff";
  let opacity = 1;
  let textDecoration = "none";

  if (resource.isCancelled) {
    backgroundColor = "var(--ion-color-danger)";
    opacity = 0.55;
    textDecoration = "line-through";
    textColor = "#ffffff";
  } else if (resource.role === "scheduler") {
    switch (resource.jobStatusSched) {
      case WORKFLOW_SCHEDULER_TYPES.Filled:
        backgroundColor = "var(--ion-color-secondwarning)";
        textColor = "#000000";
        break;
      case WORKFLOW_SCHEDULER_TYPES.Past:
        backgroundColor = "var(--ion-color-medium)";
        opacity = 0.7;
        textColor = "#111111";
        break;
      default:
        // Open
        backgroundColor = "var(--ion-color-warning)";
        textColor = "#1f1f1f";
        break;
    }
  } else {
    // Werker
    switch (resource.jobStatusWerker) {
      case WORKFLOW_WERKER_TYPES.Scheduled:
        backgroundColor = "var(--ion-color-success)";
        textColor = "#06210f";
        break;
      case WORKFLOW_WERKER_TYPES.Past:
        backgroundColor = "var(--ion-color-medium)";
        opacity = 0.7;
        textColor = "#111111";
        break;
      case WORKFLOW_WERKER_TYPES.Expired:
      case WORKFLOW_WERKER_TYPES.Uninvited:
        backgroundColor = "var(--ion-color-medium)";
        opacity = 0.45;
        textColor = "#111111";
        break;
      default:
        // Invited / available
        backgroundColor = "var(--ion-color-primary-shade)";
        textColor = "#ffffff";
        break;
    }
  }

  return {
    style: {
      backgroundColor,
      opacity,
      textDecoration,
      color: textColor,
      border: "none",
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 600,
      padding: "2px 4px",
    },
  };
}
